import { favoriteKey } from "@oshimap/domain";
export class FavoritesStore {
    #keys = new Set();
    #listeners = new Set();
    #inFlight = null;
    #ep;
    #session;
    #epoch = 0;
    #loadVersion = 0;
    #busy = new Set();
    constructor(ep, session) {
        this.#ep = ep;
        this.#session = session;
        /* 세션이 바뀔 때 한 번만 불러옵니다. 훅마다 구독하면 마운트 수만큼 나갑니다. */
        session.subscribe(() => {
            ++this.#epoch;
            ++this.#loadVersion;
            this.#inFlight = null;
            this.#busy.clear();
            this.#emit(new Set());
            void this.load();
        });
    }
    get keys() { return this.#keys; }
    subscribe(fn) {
        this.#listeners.add(fn);
        return () => { this.#listeners.delete(fn); };
    }
    has(target, id) {
        return this.#keys.has(`${target}:${id}`);
    }
    #emit(next) {
        this.#keys = next;
        for (const fn of this.#listeners)
            fn(next);
    }
    /** 테스트에서 상태를 비웁니다. 화면 코드는 쓰지 않습니다. */
    reset() {
        ++this.#epoch;
        ++this.#loadVersion;
        this.#busy.clear();
        this.#keys = new Set();
        this.#inFlight = null;
    }
    async load() {
        if (this.#session.state.status !== "authenticated") {
            this.#emit(new Set());
            return;
        }
        if (this.#busy.size > 0)
            return;
        const epoch = this.#epoch;
        const version = this.#loadVersion;
        /* 화면 셋이 동시에 마운트되면 같은 요청이 셋 나갑니다. 하나로 합칩니다. */
        this.#inFlight ??= (async () => {
            try {
                const list = await this.#ep.listFavorites();
                if (epoch === this.#epoch && version === this.#loadVersion) {
                    this.#emit(new Set(list.map((f) => favoriteKey(f))));
                }
            }
            catch { /* 실패해도 화면은 계속 쓸 수 있어야 합니다 */ }
            finally {
                if (epoch === this.#epoch && version === this.#loadVersion)
                    this.#inFlight = null;
            }
        })();
        return this.#inFlight;
    }
    /**
     * 낙관적으로 바꾸고, 실패하면 되돌립니다 (FR-SAVE-02/03 · NFR-R-03).
     * 돌려주는 값은 **바뀐 뒤의 상태**입니다.
     */
    async toggle(target, id, onNeedAuth) {
        const key = `${target}:${id}`;
        if (this.#session.state.status !== "authenticated") {
            this.#session.requireAuth?.(() => { void this.toggle(target, id).catch(() => undefined); });
            onNeedAuth?.();
            return false;
        }
        /* 닫혀 있는 렌더 시점 값이 아니라 **지금** 공유 상태를 봅니다. */
        const before = this.#keys;
        const had = before.has(key);
        if (this.#busy.has(key))
            return had;
        const epoch = this.#epoch;
        this.#busy.add(key);
        ++this.#loadVersion;
        this.#inFlight = null;
        const next = new Set(before);
        if (had)
            next.delete(key);
        else
            next.add(key);
        this.#emit(next);
        try {
            if (had)
                await this.#ep.removeFavorite(target, id);
            else
                await this.#ep.addFavorite(target, id);
            /* 로그인 직후 보류 동작이 실행되면 목록 로드와 경합합니다.
               성공 후 서버 상태로 한 번 맞춰 UI 가 뒤처지지 않게 합니다. */
            return epoch === this.#epoch ? !had : false;
        }
        catch {
            if (epoch !== this.#epoch)
                return false;
            const rollback = new Set(this.#keys);
            if (had)
                rollback.add(key);
            else
                rollback.delete(key);
            this.#emit(rollback);
            throw new Error("저장하지 못했어요. 다시 시도해 주세요.");
        }
        finally {
            if (epoch === this.#epoch) {
                this.#busy.delete(key);
                void this.load();
            }
        }
    }
}
//# sourceMappingURL=favorites.js.map